import React, { useEffect, useRef, useState } from 'react';
import {
  CreateOrganizationDto,
  InviteWorkspaceMembersMemberDto
} from '@api/Api';
import { createWorkspace, inviteWorkspaceMembers } from '@api/Workspace';

import { Carousel, Form, Button, Progress, Row, Col } from 'antd';
import { XsWidth, FromXsWidth } from '@components/Responsive';
import { ReactComponent as CircleArrowLeft } from '@assets/icons/arrow-left.svg';
import { redirectToSubdomain } from '@components/OrganizationBoundary';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@hooks/useAuth';
import LogoTabletDark from '@assets/images/logo_tablet_dark.png';
import { createOrganization } from '@api/Organization';
import { removeExtraSpaces } from '@helpers/stringTransformers';
import { useTypedSelector } from '@hooks';
import { ownOrganizationItemStateSelector } from '@redux/selectors/organizations';
import { MixpanelService, MixpanelEventType } from '@services/mixpanelService';
import moment from 'moment';
import isEmail from 'validator/es/lib/isEmail';
import InvitationStep from './InvitationStep';
import OrganizationCreationStep from './OrganizationCreationStep';

function OnboardingSteps() {
  const { user } = useAuth(true, 'internal');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const trialToken = searchParams.get('trial');
  const plan = searchParams.get('plan');
  const ownOrganization = useTypedSelector((state) =>
    ownOrganizationItemStateSelector(state)
  );
  const [isDisabled, setIsDisabled] = useState(true);
  const [currentStep, setCurrentStep] = useState(0);

  const [form] = Form.useForm();
  const { logout } = useAuth();
  const [loading, setLoading] = useState(false);
  const [loadingSkip, setLoadingSkip] = useState(false);

  const [emails, setEmails] = useState<string[]>(['', '', '']);
  const [hasEmailsErrors, setHasEmailsErrors] = useState<boolean>(false);
  const [isInviteBtnDisabled, setInviteDisableState] = useState<boolean>(true);

  const isDisabledMainBtn =
    isDisabled || loadingSkip || (isInviteBtnDisabled && currentStep === 1);
  const carouselRef = useRef<any>(null);

  useEffect(() => {
    if (ownOrganization) navigate('../');
  }, [navigate, ownOrganization]);

  const carouselStepChanged = (from: number, to: number) => {
    setCurrentStep(to);
  };

  const createOrgAndWorkspace = async ({
    name,
    subdomain
  }: CreateOrganizationDto) => {
    const {
      data: { organization }
    } = await createOrganization({
      name: removeExtraSpaces(name),
      subdomain,
      paidSubscriptionType:
        plan === 'starter' || plan === 'team'
          ? plan
          : plan === 'free'
          ? null
          : 'team',
      paidSubscriptionPeriod: 'monthly',
      trialToken
    });
    const {
      data: { workspace }
    } = await createWorkspace({
      organizationId: organization.id,
      name: removeExtraSpaces(name)
    });
    MixpanelService.track(user.id, MixpanelEventType.SIGN_UP_FOR_TRIAL, {
      email: user.email,
      name: user.name,
      registrationDate: new Date().toISOString(),
      registrationMethod:
        user.primaryProviderId === 'google-oauth2'
          ? 'google'
          : user.primaryProviderId === 'auth0'
          ? 'website'
          : 'other',
      planType: organization.planType || 'free',
      emailVerified: user.emailVerified,
      trialEndDate: organization.trialEndDate,
      trialDays: organization.trialEndDate
        ? Math.abs(
            Math.round(
              moment(organization.trialEndDate).diff(new Date(), 'days', true)
            )
          )
        : 0
    });
    return { organization, workspace };
  };

  const onFinish = async (
    payload: CreateOrganizationDto & { emails: string[] }
  ) => {
    try {
      setLoading(true);
      const emails: InviteWorkspaceMembersMemberDto[] = (payload.emails || [])
        .filter((el: string) => el && el.length > 1)
        .map((el: string) => ({
          memberIdOrEmail: el,
          role: 'workspace://member',
          customMessage: null
        }));

      const { organization, workspace } = await createOrgAndWorkspace(payload);

      if (emails.length) await inviteWorkspaceMembers(workspace.id, emails);
      redirectToSubdomain(
        organization.subdomain,
        `campaigns/all/${workspace.id}`,
        [{ name: 'welcome', value: 'true' }]
      );
    } finally {
      setLoading(false);
    }
  };

  const onSkip = async () => {
    try {
      const formValues: CreateOrganizationDto = form.getFieldsValue();
      setLoadingSkip(true);
      const { organization, workspace } = await createOrgAndWorkspace({
        name: formValues.name,
        subdomain: formValues.subdomain
      });

      redirectToSubdomain(
        organization.subdomain,
        `campaigns/all/${workspace.id}`,
        [{ name: 'welcome', value: 'true' }]
      );
    } finally {
      setLoadingSkip(false);
    }
  };

  const onFieldsChange = (_: any, all: any[]) => {
    const hasErrors = all.some(
      ({ errors }: any) => errors && errors.length > 0
    );
    setIsDisabled(hasErrors);
  };

  const onOnboradingClose = async () => {
    logout({ logoutParams: { returnTo: window.location.origin } });
  };

  useEffect(() => {
    const touchedEmails = emails.filter((email) => email && email.length > 1);
    setInviteDisableState(
      !(
        touchedEmails.length > 0 &&
        touchedEmails.every((email) => isEmail(email))
      )
    );
  }, [emails]);

  return (
    <div className="onboarding_container">
      <XsWidth>
        <div className="onboarding_container__head">
          <Progress showInfo={false} percent={(100 / 3) * (currentStep + 1)} />
          <div className="onboarding_container__controller_info">
            {currentStep + 1} / <span className="total">2</span>
          </div>
          <div className="onboarding_container__logotype">
            <img src={LogoTabletDark} alt="" />
          </div>
        </div>
      </XsWidth>
      <Form
        layout="vertical"
        form={form}
        onFinish={onFinish}
        onFieldsChange={onFieldsChange}
        requiredMark={false}
      >
        <Carousel
          ref={carouselRef}
          dots={false}
          effect="fade"
          swipe={!isDisabled}
          beforeChange={carouselStepChanged}
        >
          <div className="onboarding_container__step onboarding_container__step--creation">
            <FromXsWidth>
              <Row justify="center">
                <Col>
                  <div className="onboarding_container__controller_info">
                    Step: {currentStep + 1} / <span className="total">2</span>
                  </div>
                </Col>
              </Row>
            </FromXsWidth>
            <OrganizationCreationStep
              form={form}
              setIsDisabled={setIsDisabled}
            />
          </div>
          <div className="onboarding_container__step onboarding_container__step--invitation">
            <FromXsWidth>
              <Row justify="center">
                <Col>
                  <div className="onboarding_container__controller_info">
                    Step: {currentStep + 1} / <span className="total">2</span>
                  </div>
                </Col>
              </Row>
            </FromXsWidth>
            <InvitationStep
              form={form}
              loading={loading}
              setEmails={setEmails}
              emails={emails}
              hasErrors={hasEmailsErrors}
              setHasErrors={setHasEmailsErrors}
            />
          </div>
        </Carousel>
        <div className="onboarding_container__controller">
          <XsWidth>
            {currentStep === 0 ? (
              <Button
                className="onboarding_container__controller_button onboarding_container__controller_button--prev"
                type="text"
                onClick={() => onOnboradingClose()}
              >
                <div className="icon">
                  <CircleArrowLeft style={{ position: 'relative' }} />
                </div>
                Back to sign in
              </Button>
            ) : (
              <Button
                className="onboarding_container__controller_button onboarding_container__controller_button--prev"
                type="text"
                onClick={() => carouselRef.current.prev()}
              >
                <div className="icon">
                  <CircleArrowLeft style={{ position: 'relative' }} />
                </div>
                Back
              </Button>
            )}
          </XsWidth>
          <XsWidth>
            {currentStep === 1 && (
              <Button
                loading={loadingSkip}
                disabled={loading}
                className="onboarding_container__controller_button"
                htmlType="button"
                type="text"
                style={{
                  minWidth: 100,
                  height: 48
                }}
                onClick={onSkip}
              >
                Skip
              </Button>
            )}
            <Button
              loading={loading}
              className="onboarding_container__controller_button"
              type="primary"
              htmlType={currentStep === 1 ? 'submit' : 'button'}
              onClick={() => currentStep !== 1 && carouselRef.current.next()}
              disabled={isDisabledMainBtn}
            >
              {currentStep === 1 ? 'Send invites' : 'Next'}
            </Button>
          </XsWidth>
          <FromXsWidth>
            <Button
              className="onboarding_container__controller_button onboarding_container__controller_button--prev"
              type="text"
              onClick={() => {
                if (currentStep !== 0) carouselRef.current.prev();
                else onOnboradingClose();
                if (document.activeElement instanceof HTMLElement) {
                  document.activeElement.blur();
                }
              }}
            >
              <div className="icon">
                <CircleArrowLeft />
              </div>
              Back
            </Button>
            <div className="onboarding_container__controller-buttons">
              {currentStep === 1 && (
                <Button
                  loading={loadingSkip}
                  disabled={loading}
                  htmlType="button"
                  onClick={onSkip}
                  style={{
                    minWidth: 100
                  }}
                >
                  Skip
                </Button>
              )}
              <Button
                loading={loading}
                type="primary"
                htmlType={currentStep === 1 ? 'submit' : 'button'}
                onClick={() => {
                  if (currentStep !== 1) carouselRef.current.next();
                  if (document.activeElement instanceof HTMLElement) {
                    document.activeElement.blur();
                  }
                }}
                disabled={isDisabledMainBtn}
                style={{
                  minWidth: currentStep === 1 ? 144 : 100
                }}
              >
                {currentStep === 1 ? 'Send invites' : 'Next'}
              </Button>
            </div>
          </FromXsWidth>
        </div>
      </Form>
    </div>
  );
}

export default OnboardingSteps;
